import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Typography,
  Badge,
  Menu,
  MenuItem,
  ButtonBase,
  Button,
  Tooltip,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import ProfileIcon from "../../../assets/icons/profile-with-round-grey.svg";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import "./profile-header.scss";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import MenuIcon from "@mui/icons-material/Menu";
import hamburgerIcon from "../../../assets/icons/hamburger.svg";
import {
  superAdminLogout,
  providerLogout,
  preNeedLogout,
} from "../../routines/user-operations";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { routes } from "../../../constants/routes";
import AlertComponent from "../alert-component/alert-component";
import ActionConfirmationPopup from "../action-confirmation-popup/action-confirmation-popup";
import { ProfileHeaderProps } from "./profile-header-props";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Stack from "@mui/material/Stack";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import { ReactComponent as UserIcon } from "../../../assets/icons/user-profile-icon.svg";
import { getProviderProfileDetails } from "../../../api/provider-section/provider-profile.api";
import { getPreneedProfileDetails } from "../../../api/preneed-section/preneed-profile.api";
import { getCurrentUserDetails } from "../../routines/user-operations";
import { getAdminProfile } from "../../../api/admin/admin-profile.api";

const ProfileHeader: React.FC<ProfileHeaderProps> = ({
  breadcrumbLink,
  showBackArrow,
  handleBackArrowClick,
  mode,
}) => {
  const userDetails = useSelector((state: any) => {
    return mode === "provider"
      ? state.session.providerDetails
      : mode === "preNeed"
        ? state.session.preNeedDetails
        : state.session.superAdminDetails;
  });

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isLogout, setIsLogout] = useState<boolean>(false);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<
    "error" | "success" | "info" | "warning"
  >("info");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const location = useLocation();

  const fetchProfile = async () => {
    try {
      const isSuperAdmin = !mode || mode === "admin";
      if (!mode && !isSuperAdmin) return;

      const userAuth = getCurrentUserDetails(isSuperAdmin ? "superAdmin" : mode);
      const token = userAuth?.accessToken || userAuth?.token;

      if (!token) return;

      let response;
      if (mode === "provider") {
        response = await getProviderProfileDetails(token);
      } else if (mode === "preNeed") {
        response = await getPreneedProfileDetails(token);
      } else if (isSuperAdmin) {
        response = await getAdminProfile({ timezone: Intl.DateTimeFormat().resolvedOptions().timeZone });
      }

      if (response?.data?.success || response?.data?.data) {
        const profileData = response.data.data || {};

        const normalizedProfilePhoto =
          profileData?.profile_photo ||
          profileData?.profileImage ||
          profileData?.profilePhoto ||
          profileData?.user?.profile_photo ||
          profileData?.user?.profileImage ||
          profileData?.user?.profilePhoto ||
          "";

        const actionType =
          mode === "provider"
            ? "SESSION:UPDATE_PROVIDER_DETAILS"
            : mode === "preNeed"
              ? "SESSION:UPDATE_PRENEED_DETAILS"
              : "SESSION:UPDATE_SUPER_ADMIN_DETAILS";

        dispatch({
          type: actionType,
          payload: {
            ...profileData,
            first_name:
              profileData?.first_name ||
              profileData?.firstName ||
              profileData?.user?.first_name ||
              profileData?.fullName?.split(" ")?.[0] ||
              "",
            last_name:
              profileData?.last_name ||
              profileData?.lastName ||
              profileData?.user?.last_name ||
              profileData?.fullName?.split(" ")?.slice(1).join(" ") ||
              "",
            profile_photo: normalizedProfilePhoto,
            profileImage: normalizedProfilePhoto,
            profilePhoto: normalizedProfilePhoto,
          },
        });
      }
    } catch (error) {
      console.error(
        `FAILED TO FETCH ${mode?.toUpperCase() || "ADMIN"} PROFILE IN HEADER::`,
        error
      );
    }
  };

  useEffect(() => {
    fetchProfile();
  }, [mode]);

  const isProviderProfileListing = location.pathname === routes.PROVIDER_PROFILES;

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (
      e.key === "Enter" ||
      e.key === " " ||
      e.key === "ArrowDown" ||
      e.key === "ArrowUp"
    ) {
      e.preventDefault();
      setAnchorEl(e.currentTarget as HTMLElement);
    }

    if (e.key === "Escape") handleClose();
  };

  const handleLogout = async () => {
    try {
      if (mode === "provider") {
        providerLogout();
        navigate(routes.PROVIDER_LOGIN, {
          replace: true,
          state: {
            fromLogout: true,
            alertMessage: "Provider has logged out successfully.",
            alertType: "success",
          },
        });
      } else if (mode === "preNeed") {
        preNeedLogout();
        navigate(routes.PRENEED_LOGIN, {
          replace: true,
          state: {
            fromLogout: true,
            alertMessage: "Preneed has logged out successfully.",
            alertType: "success",
          },
        });
      } else {
        superAdminLogout();
        navigate(routes.ADMIN_LOGIN, {
          replace: true,
          state: {
            fromLogout: true,
            alertMessage: "Admin has logged out successfully.",
            alertType: "success",
          },
        });
      }

      handleClose();
    } catch (error) {
      console.error("Logout error: ", error);
      setAlertOpen(true);
      setAlertMessage("Failed to logout");
      setAlertType("error");
    }
  };

  const finalUserName = () => {
    const first = userDetails?.first_name || userDetails?.user?.first_name || "";
    const last = userDetails?.last_name || userDetails?.user?.last_name || "";
    const full = userDetails?.fullName || userDetails?.user?.fullName || "";
    const computedName = `${first} ${last}`.trim() || full.trim();
    return computedName || "User Name";
  };

  const finalUserEmail = () => {
    return userDetails?.email || userDetails?.user?.email || "";
  };

  const finalProfilePhoto =
    userDetails?.profile_photo ||
    userDetails?.profileImage ||
    userDetails?.profilePhoto ||
    userDetails?.user?.profile_photo ||
    userDetails?.user?.profileImage ||
    userDetails?.user?.profilePhoto ||
    "";

  return (
    <AppBar
      position="sticky"
      sx={{
        backgroundColor: "#fff",
        boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
        top: 0,
        zIndex: 1100,
        borderBottom: "1px solid #E9E9E8",
        width: "100%",
        // Force fixed on mobile to ensure it sticks to viewport top
        position: { xs: "fixed", lg: "sticky" },
      }}
      className="profile-sticky-header"
    >
      <Toolbar sx={{ py: 1, px: { xs: 1.5, sm: 3 } }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            justifyContent: "space-between",
          }}
        >
          {/* LEFT SECTION: Hamburger, Breadcrumbs and Back */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flex: 1, minWidth: 0 }}>
            {/* Hamburger Menu for Mobile/Tablet */}
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={(e) => {
                e.stopPropagation();
                dispatch({ type: "APPEARANCE:TOGGLE_ADMIN_SIDEBAR", payload: {} });
              }}
              sx={{
                display: { xs: "flex", lg: "none" },
                color: "#15585D",
                padding: "8px",
                marginRight: "8px",
                "&:hover": { backgroundColor: "rgba(21, 88, 93, 0.08)" },
              }}
            >
              <MenuIcon />
            </IconButton>

            {showBackArrow && (
              <IconButton
                onClick={() => {
                  if (handleBackArrowClick) handleBackArrowClick();
                  else navigate(-1);
                }}
                aria-label="Go Back"
                className="back-arrow-button"
                sx={{ color: "#15585D", padding: "8px" }}
              >
                <ArrowBackOutlinedIcon />
              </IconButton>
            )}

            <Box className="breadcrumb-wrapper" sx={{ flex: 1, minWidth: 0, ml: showBackArrow ? 0 : 0.5, overflowX: "auto", overflowY: "hidden", "&::-webkit-scrollbar": { display: "none" }, msOverflowStyle: "none", scrollbarWidth: "none" }}>
              <Breadcrumbs
                separator={<NavigateNextIcon sx={{ fontSize: "18px", color: "#B0B0B0" }} />}
                aria-label="breadcrumb"
                sx={{
                  "& .MuiBreadcrumbs-ol": { flexWrap: "nowrap", alignItems: "center" },
                  "& .MuiBreadcrumbs-li": { flexShrink: 0, whiteSpace: "nowrap" },
                }}
              >
                {breadcrumbLink?.map((linkData: any, index: any) => {
                  const isLast = index === breadcrumbLink.length - 1;
                  const isFirst = index === 0;

                  const disableFirstLink = isProviderProfileListing && isFirst;

                  if (isLast) {
                    return (
                      <Typography
                        key={index}
                        sx={{
                          color: "#15585D",
                          fontWeight: 700,
                          fontSize: { xs: "18px", md: "22px" },
                          letterSpacing: "-0.5px",
                          whiteSpace: "nowrap",
                          lineHeight: 1.2,
                        }}
                      >
                        {linkData?.label}
                      </Typography>
                    );
                  }

                  if (linkData?.disablePointerEvents || disableFirstLink) {
                    return (
                      <Typography
                        key={index}
                        sx={{
                          color: "#999",
                          cursor: "default",
                          pointerEvents: "none",
                          fontSize: { xs: "14px", md: "16px" },
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {linkData.label}
                      </Typography>
                    );
                  }

                  if (linkData?.handleClick) {
                    return (
                      <Box
                        key={index}
                        component="span"
                        className="breadcrumb-link"
                        onClick={linkData.handleClick}
                        sx={{
                          cursor: "pointer",
                          color: "#666",
                          fontSize: { xs: "14px", md: "16px" },
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                          "&:hover": { color: "#15585D" },
                        }}
                      >
                        {linkData.label}
                      </Box>
                    );
                  }

                  if (linkData?.link) {
                    return (
                      <Link
                        key={index}
                        to={linkData.link}
                        className="breadcrumb-link"
                        state={linkData.stateData ? linkData.stateData : {}}
                        style={{
                          textDecoration: "none",
                          color: "#666",
                          fontSize: "14px",
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {linkData.label}
                      </Link>
                    );
                  }

                  return (
                    <Typography
                      key={index}
                      sx={{
                        color: "#666",
                        fontSize: { xs: "14px", md: "16px" },
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {linkData.label}
                    </Typography>
                  );
                })}
              </Breadcrumbs>
            </Box>
          </Box>
          {/* RIGHT SECTION: Notifications + Profile Trigger */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ButtonBase
              onClick={handleOpen}
              onKeyDown={handleKeyDown}
              focusRipple
              sx={{
                display: "flex",
                alignItems: "center",
                textAlign: "left",
                gap: 1.2,
                px: 1.2,
                py: 0.8,
                borderRadius: 2,
                border: "1px solid #E9E9E8",
                background: "#fff",
                transition: "0.2s",
                "&:hover": { background: "#F7F8FB", borderColor: "#15585D" },
              }}
              className="profile-header-dropdown"
            >
              <Box
                component="img"
                src={finalProfilePhoto || ProfileIcon}
                key={finalProfilePhoto}
                alt="profile avatar"
                sx={{
                  width: { xs: 32, sm: 36 },
                  height: { xs: 32, sm: 36 },
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
              />

              <Box 
                className="profile-dropdown-info"
                sx={{ display: { xs: "none", sm: "flex" }, flexDirection: "column", alignItems: "flex-start" }}
              >
                <Typography sx={{ fontWeight: 600, color: "#15585D", fontSize: "0.9rem", lineHeight: 1.2 }}>
                  {finalUserName()}
                </Typography>
                <Typography sx={{ color: "#666", fontSize: "0.75rem", fontWeight: 500 }}>
                  {userDetails?.user?.role === "Super Admin" ? "Admin" : (userDetails?.user?.role || "Admin")}
                </Typography>
              </Box>

              <KeyboardArrowDownIcon
                sx={{
                  fontSize: "1.2rem",
                  color: "#555",
                  transition: "transform 0.25s ease",
                  transform: open ? "rotate(180deg)" : "rotate(0deg)",
                  ml: 0.5,
                }}
              />
            </ButtonBase>
          </Box>
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          MenuListProps={{ autoFocus: true }}
          PaperProps={{
            sx: {
              minWidth: 280,
              borderRadius: "12px",
              p: 1,
              boxShadow: "0px 10px 30px rgba(0,0,0,0.1)",
              bgcolor: "#fff",
              mt: 1,
              border: "1px solid #E9E9E8",
            },
          }}
        >
          <MenuItem
            disableRipple
            sx={{
              p: 0,
              "&:hover": { background: "transparent" },
              "&.Mui-focusVisible": { background: "transparent" },
            }}
          >
            <Button
              onClick={() => {
                handleClose();
                if (mode === "provider") {
                  navigate(routes.PROVIDER_PROFILE);
                } else if (mode === "preNeed") {
                  navigate(routes.PRENEED_PROFILE);
                } else {
                  navigate(routes.ADMIN_PROFILE_MANAGEMENT);
                }
              }}
              sx={{
                width: "100%",
                justifyContent: "flex-start",
                textTransform: "none",
                gap: 1.5,
                p: 1.5,
                borderRadius: "8px",
                bgcolor: "#F7F8FB",
                color: "#15585D !important",
                "&:hover": { bgcolor: "#E9EFF0" },
              }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  overflow: "hidden",
                  border: "1px solid #E0E0E0",
                }}
              >
                {finalProfilePhoto ? (
                  <Box
                    component="img"
                    src={finalProfilePhoto}
                    key={finalProfilePhoto}
                    alt="Profile"
                    sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <UserIcon />
                )}
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {finalUserName()}
                </Typography>
                <Typography sx={{ fontSize: "0.8rem", color: "#666", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {finalUserEmail() || (userDetails?.user?.role === "Super Admin" ? "Admin" : (userDetails?.user?.role || "Role"))}
                </Typography>
              </Box>
            </Button>
          </MenuItem>

          <MenuItem
            onClick={() => {
              handleClose();
              setIsLogout(true);
            }}
            sx={{
              mt: 1,
              width: "100%",
              p: 0,
              "&:hover": { background: "transparent" },
            }}
          >
            <Box
              sx={{
                width: "100%",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid #F3C3C2",
                color: "#F15B59",
                bgcolor: "#FFF5F5",
                py: 1,
                "&:hover": { bgcolor: "#FFEAEA" },
              }}
            >
              <Typography sx={{ fontWeight: 600, fontSize: "0.9rem" }}>Logout</Typography>
            </Box>
          </MenuItem>
        </Menu>

        <ActionConfirmationPopup
          title="Log Out"
          note="Are you sure you want to Log Out?"
          open={isLogout}
          onClose={() => setIsLogout(false)}
          onConfirm={handleLogout}
          cancelButtonText="No"
          confirmButtonText="Yes"
          type="logout"
        />

        <AlertComponent
          message={alertMessage}
          type={alertType ?? "info"}
          open={alertOpen}
          onClose={() => setAlertOpen(false)}
        />
      </Toolbar>
    </AppBar>
  );
};

export default ProfileHeader;